import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Users,
  TrendingUp,
  Link as LinkIcon,
  Copy,
  CheckCircle,
  AlertCircle,
  DollarSign,
  UserPlus,
  Lock,
  CreditCard,
  ArrowRight
} from 'lucide-react';

interface AffiliateStats {
  referral_code: string;
  level: string;
  total_referrals: number;
  active_referrals: number;
  total_earnings: number;
  referrer_email: string | null;
  referrer_name: string | null;
}

interface ReferredUser {
  id: string;
  email: string;
  full_name: string | null;
  status: string;
  commission: number;
  created_at: string;
}

const Affiliates: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [referredUsers, setReferredUsers] = useState<ReferredUser[]>([]);
  const [copied, setCopied] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);

  useEffect(() => {
    fetchAffiliateData();
  }, []);

  const fetchAffiliateData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check subscription status
      const { data: subData } = await supabase
        .from('subscriptions')
        .select('status')
        .eq('user_id', user.id)
        .maybeSingle();

      const isActive = subData?.status === 'active';
      setHasSubscription(isActive);

      if (!isActive) {
        setLoading(false);
        return;
      }

      // Get affiliate stats
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_affiliate_stats', { user_id_param: user.id });

      if (statsError) throw statsError;
      setStats(statsData[0] || null);

      // Get referred users
      const { data: usersData, error: usersError } = await supabase
        .rpc('get_referred_users_status', { user_id_param: user.id });

      if (usersError) throw usersError;
      setReferredUsers(usersData || []);
    } catch (err: any) {
      console.error('Error fetching affiliate data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    if (!stats?.referral_code) return;
    
    try {
      const referralLink = `${window.location.origin}/register?ref=${stats.referral_code}`;
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-8 sm:py-16">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-white/5 rounded-2xl">
              <Lock className="h-12 w-12 text-gray-400" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            Suscripción requerida
          </h1>
          <p className="text-gray-300 mb-3">
            El programa de embajadores está disponible para suscriptores activos.
          </p>
          <p className="text-gray-400 text-sm mb-8">
            Solo puedes recomendar una herramienta que utilizas. Activa tu suscripción
            para acceder a tu enlace de referido y empezar a ganar comisiones.
          </p>
          <Link
            to="/dashboard/subscription"
            className="btn-primary inline-flex items-center space-x-2 text-lg px-8 py-3"
          >
            <CreditCard className="h-5 w-5" />
            <span>Ver planes</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center">
            <Users className="h-8 w-8 text-primary mr-3" />
            Programa de Embajadores
          </h1>
          <p className="mt-2 text-gray-400">
            Comparte Asistente Trading IA y gana comisiones por cada referido
          </p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <UserPlus className="h-8 w-8 text-blue-500" />
              <span className="text-3xl font-bold text-white">
                {stats?.total_referrals || 0}
              </span>
            </div>
            <h3 className="text-lg font-medium text-white">Referidos Totales</h3>
            <p className="mt-1 text-sm text-gray-400">usuarios que has invitado</p>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <Users className="h-8 w-8 text-green-500" />
              <span className="text-3xl font-bold text-white">
                {stats?.active_referrals || 0}
              </span>
            </div>
            <h3 className="text-lg font-medium text-white">Referidos Activos</h3>
            <p className="mt-1 text-sm text-gray-400">con suscripción activa</p>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="h-8 w-8 text-yellow-500" />
              <span className="text-xl font-bold text-white">
                {stats?.level || 'Trader'}
              </span>
            </div>
            <h3 className="text-lg font-medium text-white">Nivel</h3>
            <p className="mt-1 text-sm text-gray-400">
              {stats?.level === 'Trader Pro' ? '50% por referido' : '25% por referido'}
            </p>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="h-8 w-8 text-purple-500" />
              <span className="text-3xl font-bold text-white">
                €{stats?.total_earnings?.toFixed(2) || '0.00'}
              </span>
            </div>
            <h3 className="text-lg font-medium text-white">Ganancias Totales</h3>
            <p className="mt-1 text-sm text-gray-400">comisiones acumuladas</p>
          </div>
        </div>

        {/* Referral Link */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Tu Enlace de Embajador</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 bg-dark-overlay rounded-lg p-4 overflow-hidden">
              <div className="flex items-center space-x-2">
                <LinkIcon className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-gray-300 text-sm truncate">
                  {window.location.origin}/register?ref={stats?.referral_code}
                </span>
              </div>
            </div>
            <button
              onClick={copyReferralLink}
              className="btn-primary flex items-center justify-center space-x-2 whitespace-nowrap"
            >
              {copied ? (
                <>
                  <CheckCircle className="h-5 w-5" />
                  <span>¡Copiado!</span>
                </>
              ) : (
                <>
                  <Copy className="h-5 w-5" />
                  <span>Copiar</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Referred Users */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-white">Usuarios Invitados</h3>

          {referredUsers.length === 0 ? (
            <div className="card py-8 text-center text-gray-400">
              No hay usuarios invitados aún
            </div>
          ) : (
            <div className="space-y-3">
              {referredUsers.map((user) => (
                <div key={user.id} className="card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="text-white font-medium">{user.full_name || 'Usuario'}</p>
                    <p className="text-sm text-gray-400">{user.email}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      user.status === 'active'
                        ? 'bg-green-500/20 text-green-500'
                        : 'bg-gray-500/20 text-gray-500'
                    }`}>
                      {user.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                    <span className="text-white font-semibold text-sm">€{user.commission.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Referrer Info */}
        {stats?.referrer_email && (
          <div className="card">
            <h3 className="text-lg font-semibold text-white mb-4">Invitado por</h3>
            <p className="text-gray-300">
              Fuiste invitado por:{' '}
              <span className="text-white font-medium">
                {stats.referrer_name || stats.referrer_email}
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Affiliates;